import { VorgangProtokollAction, VorgangProzessSchritt } from '@core/domain';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { TimelineLayout } from '@/components/ui/timeline/timeline-layout';
import { getTimelineItemForAussteuerung } from '@/features/vorgangHistorie/getTimelineItemForAussteuerung';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { VorgangDetail, VorgangDetailProtokoll } from '@/types';
import { getTimelineItemForAbarbeitung } from './getTimelineItemForAbarbeitung';
import { getTimelineItemForAbarbeitungsSchritt } from './getTimelineItemForAbarbeitungsSchritt';
import { getTimelineItemForVorgangProtokoll } from './getTimelineItemForVorgangProtokoll';

interface VorgangHistorieProps {
  vorgang: VorgangDetail;
  protokoll: VorgangDetailProtokoll;
}

export const VorgangHistorie: React.FC<VorgangHistorieProps> = ({
  vorgang,
  protokoll,
}) => {
  const navigate = useNavigate();
  const archivierungEntkoppelnAktiv = useFeatureFlag()(
    'CORE_7492_ARCHIVIERUNG_ENTKOPPELN',
  );
  const location = useLocation();
  const urlParams = useParams<{
    abarbeitungId: string;
    schrittId: string;
  }>();

  const protokollItems = protokoll.flatMap(getTimelineItemForVorgangProtokoll);

  const zwischenSpeicherItems = protokoll.filter(
    (item) => item.action === VorgangProtokollAction.SCHRITT_ZWISCHENSPEICHERN,
  );

  const abarbeitungItems = vorgang.abarbeitungen.map((abarbeitung) =>
    getTimelineItemForAbarbeitung(vorgang, abarbeitung),
  );

  const abarbeitungsSchrittItems = vorgang.abarbeitungen
    .flatMap((abarbeitung) =>
      abarbeitung.schritte.map((schritt, index) => {
        const timelineItem = getTimelineItemForAbarbeitungsSchritt({
          vorgang,
          abarbeitung,
          schritt,
          zwischenSpeicherungen: zwischenSpeicherItems.filter(
            (item) => item.details.schrittId === schritt.id,
          ),
          navigate,
          locationState: location.state,
          urlParams,
          archivierungEntkoppelnAktiv,
          /**
           * Um klarer zu signalisieren, welcher Abarbeitungs-Schritt
           * erneut angestoßen wurde, wird beim ersten Schritt der Initiator
           * der Abarbeitung gesetzt.
           */
          initiator: index === 0 ? abarbeitung.initiator : 'system',
        });

        if (
          (schritt.schritt as VorgangProzessSchritt) ===
          VorgangProzessSchritt.FACHLICHE_VALIDIERUNG
        ) {
          return [timelineItem, getTimelineItemForAussteuerung(schritt)];
        } else {
          return timelineItem;
        }
      }),
    )
    .flat();

  const items = [
    ...protokollItems,
    ...abarbeitungsSchrittItems,
    ...abarbeitungItems,
  ]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .map((item, index) => ({
      ...item,
      id: index,
    }));

  return <TimelineLayout items={items} />;
};
